import React, { useCallback, useMemo } from 'react';

import { Autocomplete, FormControlLabel, TextField } from '@mui/material';

import { TaskType } from '@liscio/api';

import { Select } from 'components/Select';
import TASK_TYPES from 'constants/taskTypes';
import { useFirmViewMode } from 'custom-hooks/useFirmViewMode';

// NOTE: We only allow the following two task types for mobile
const MOBILE_CREATE_TASK_TYPES = [TaskType.RequestInformation, TaskType.ToDo];
const mobileTaskOptions = TASK_TYPES.filter((option) =>
  MOBILE_CREATE_TASK_TYPES.includes(option.value as TaskType)
);

export interface TaskTypeSelectProps {
  value: string;
  onChange?: (taskType: string) => void;
}

export const TaskTypeSelect = ({ value, onChange }: TaskTypeSelectProps) => {
  const { isDesktop } = useFirmViewMode();
  const taskValue = useMemo(
    () => mobileTaskOptions.find((task) => task.value === value),
    [value]
  );
  const handleTaskChange = useCallback(
    (taskOption: RichOption | null) => {
      if (onChange && taskOption) {
        onChange(String(taskOption.value));
      }
    },
    [onChange]
  );

  // Desktop: Autocomplete
  if (isDesktop) {
    return (
      <FormControlLabel
        label="Task"
        labelPlacement="top"
        control={
          <Autocomplete
            fullWidth
            options={mobileTaskOptions}
            value={taskValue || undefined}
            onChange={(_event, newValue) => handleTaskChange(newValue)}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            disableClearable
            renderInput={(params) => (
              <TextField {...params} placeholder="Select task type..." />
            )}
          />
        }
        sx={{ width: '100%', alignItems: 'flex-start', margin: 0 }}
      />
    );
  }

  // Mobile: Full-screen Select
  return (
    <FormControlLabel
      label="Task Type"
      labelPlacement="top"
      sx={{ width: '100%', display: 'flex' }}
      control={
        <Select
          title="Task Type"
          data={mobileTaskOptions}
          onChange={handleTaskChange}
          value={taskValue}
          clearable={false}
          btnProps={{
            fullWidth: true,
            variant: 'text',
            sx: { justifyContent: 'space-between' },
          }}
          width="100%"
        />
      }
    />
  );
};
